import { useFormContext } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FormField } from "./FormField";
import { LockToggle } from "./LockToggle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWalletState } from "@/hooks/useWalletState";
import { useState } from 'react';

export function TaxSettings() {
  const { watch, setValue } = useFormContext();
  const { isTestnet } = useWalletState();
  const [isOpen, setIsOpen] = useState(false);

  const formValues = watch();
  const buyTax = formValues.buyTax || "0";
  const sellTax = formValues.sellTax || "0";
  const customBuyTax = formValues.customBuyTax || "";
  const customSellTax = formValues.customSellTax || "";
  const buyTaxLocked = formValues.buyTaxLocked || false;
  const sellTaxLocked = formValues.sellTaxLocked || false;

  const validateTaxValue = (value: string) => {
    // Allow empty string or valid numbers
    if (value === "") return "";
    
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 25) {
      return "";
    }
    return numValue.toString(); // Convert back to string to remove leading zeros
  };

  const handleCustomTaxChange = (type: 'buy' | 'sell', value: string) => {
    const validValue = validateTaxValue(value);
    if (type === 'buy') {
      setValue("customBuyTax", value); // Store raw input value
      setValue("buyTax", "custom"); // Keep "custom" selected
    } else {
      setValue("customSellTax", value); // Store raw input value
      setValue("sellTax", "custom"); // Keep "custom" selected
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="flex items-center justify-between p-6">
        <div>
          <h3 className="text-lg font-semibold">Tax Collection</h3>
          <p className="text-sm text-muted-foreground">Configure your token's buy and sell tax rates</p>
        </div>
        <span
          className={`transform transition-transform duration-200 cursor-pointer ${isOpen ? 'rotate-180' : ''}`}
          style={{
            color: `var(--color-secondary, #10b981)`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          ▼
        </span>
      </div>
      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-6 pt-0 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Buy Tax */}
            <FormField
              label={
                <div className="flex items-center justify-between">
                  <span>Buy Tax</span>
                  {isTestnet && (
                    <LockToggle
                      locked={buyTaxLocked}
                      onToggle={(locked) => setValue("buyTaxLocked", locked)}
                      tooltip="If locked, buy tax settings cannot be changed after token deployment"
                    />
                  )}
                </div>
              }
              tooltip="The percentage of tax applied to buy transactions (0-25%)"
              input={
                <div className="space-y-2">
                  <Select
                    value={buyTax}
                    onValueChange={(value) => {
                      setValue("buyTax", value);
                      if (value === "custom") {
                        setValue("customBuyTax", ""); // Reset custom value when switching to custom
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select buy tax" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="1">1%</SelectItem>
                      <SelectItem value="2">2%</SelectItem>
                      <SelectItem value="3">3%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {buyTax === "custom" && (
                    <Input
                      type="number"
                      placeholder="Enter custom buy tax (0-25%)"
                      value={customBuyTax}
                      min="0"
                      max="25"
                      step="0.1"
                      onChange={(e) => handleCustomTaxChange('buy', e.target.value)}
                    />
                  )}
                </div>
              }
            />

            {/* Sell Tax */}
            <FormField
              label={
                <div className="flex items-center justify-between">
                  <span>Sell Tax</span>
                  {isTestnet && (
                    <LockToggle
                      locked={sellTaxLocked}
                      onToggle={(locked) => setValue("sellTaxLocked", locked)}
                      tooltip="If locked, sell tax settings cannot be changed after token deployment"
                    />
                  )}
                </div>
              }
              tooltip="The percentage of tax applied to sell transactions (0-25%)"
              input={
                <div className="space-y-2">
                  <Select
                    value={sellTax}
                    onValueChange={(value) => {
                      setValue("sellTax", value);
                      if (value === "custom") {
                        setValue("customSellTax", ""); // Reset custom value when switching to custom
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sell tax" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="1">1%</SelectItem>
                      <SelectItem value="2">2%</SelectItem>
                      <SelectItem value="3">3%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {sellTax === "custom" && (
                    <Input
                      type="number"
                      placeholder="Enter custom sell tax (0-25%)"
                      value={customSellTax}
                      min="0"
                      max="25"
                      step="0.1"
                      onChange={(e) => handleCustomTaxChange('sell', e.target.value)}
                    />
                  )}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
